import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { societyInfo } from '../data/mock';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const [leadership, setLeadership] = useState([]);
  const [loadingLeadership, setLoadingLeadership] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchLeadership();
  }, []);

  const fetchLeadership = async () => {
    try {
      const response = await axios.get(`${API}/leadership`);
      if (response.data && response.data.length > 0) {
        setLeadership(response.data);
      } else {
        // Seed leadership if empty
        await axios.post(`${API}/seed-leadership`);
        const retryResponse = await axios.get(`${API}/leadership`);
        setLeadership(retryResponse.data);
      }
    } catch (error) {
      console.error('Error fetching leadership:', error);
      // Fallback to mock data if API fails
      setLeadership([]);
    } finally {
      setLoadingLeadership(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      toast.success(
        language === 'sl'
          ? 'Sporočilo uspešno poslano!'
          : 'Message sent successfully!'
      );

      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(
        language === 'sl'
          ? 'Napaka pri pošiljanju sporočila. Poskusite znova.'
          : 'Error sending message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t.contact.address,
      value: societyInfo.address,
      href: `https://maps.google.com/?q=${encodeURIComponent(societyInfo.address)}`,
    },
    {
      icon: Phone,
      label: t.contact.phone,
      value: societyInfo.phone,
      href: `tel:${societyInfo.phone}`,
    },
    {
      icon: Mail,
      label: t.contact.email,
      value: societyInfo.email,
      href: `mailto:${societyInfo.email}`,
    },
  ];

  return (
    <section id="contact" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-4">
            <Mail className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-wider uppercase">
              {t.contact.title}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">
            {t.contact.title}
          </h2>
        </div>

        {/* Leadership/Contacts Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
            <Users className="w-6 h-6 text-emerald-400" />
            {language === 'sl' ? 'Vodstvo družine' : 'Society Leadership'}
          </h3>
          {loadingLeadership ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {leadership.map((member, index) => (
                <Card
                  key={member.id || index}
                  className="bg-stone-800/50 border-stone-700/50 p-4 text-center hover:bg-stone-800 hover:border-emerald-600/30 transition-all duration-300 group"
                  data-testid={`leadership-card-${index}`}
                >
                  <Avatar className="w-16 h-16 mx-auto mb-3 ring-2 ring-stone-700 group-hover:ring-emerald-600/30 transition-all duration-300">
                    {member.image ? (
                      <AvatarImage 
                        src={member.image} 
                        alt={member.name}
                        className="object-cover"
                      />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white text-lg font-bold">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <h4 className="text-sm font-semibold text-white mb-1 leading-tight">
                    {member.name}
                  </h4>
                  <p className="text-emerald-400 text-xs font-medium mb-2 leading-tight">
                    {language === 'sl' ? member.role_sl : member.role_en}
                  </p>
                  <a 
                    href={`tel:${member.phone.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-emerald-400 transition-colors"
                  >
                    <Phone className="w-3 h-3" />
                    <span>{member.phone}</span>
                  </a>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info and Form */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-white mb-6">
              {language === 'sl' ? 'Kontaktni podatki' : 'Contact Information'}
            </h3>
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <a
                  key={index}
                  href={info.href}
                  target={info.icon === MapPin ? '_blank' : undefined}
                  rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                  className="block"
                >
                  <Card className="bg-stone-800/50 border-stone-700/50 p-5 hover:bg-stone-800 hover:border-emerald-600/30 transition-all duration-300 group">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center group-hover:bg-emerald-600/30 transition-colors">
                        <info.icon className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-sm text-stone-500 mb-1">
                          {info.label}
                        </div>
                        <div className="text-white font-medium group-hover:text-emerald-300 transition-colors">
                          {info.value}
                        </div>
                      </div>
                    </div>
                  </Card>
                </a>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-stone-800/50 border-stone-700/50 p-8">
            <h3 className="text-xl font-semibold text-white mb-6">
              {t.contact.sendMessage}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-stone-300">
                  {t.contact.name}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="bg-stone-900/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder={language === 'sl' ? 'Vaše ime' : 'Your name'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-300">
                  {t.contact.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="bg-stone-900/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder={language === 'sl' ? 'vaš-email@primer.si' : 'your-email@example.com'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-stone-300">
                  {t.contact.message}
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  required
                  rows={5}
                  className="bg-stone-900/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-emerald-500 focus:ring-emerald-500/20 resize-none"
                  placeholder={language === 'sl' ? 'Vaše sporočilo...' : 'Your message...'}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-6 font-medium rounded-xl shadow-lg shadow-emerald-600/25 hover:shadow-emerald-500/40 transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {language === 'sl' ? 'Pošiljam...' : 'Sending...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    {t.contact.send}
                  </span>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
